import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import TagInput from './TagInput';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from './ui/command';
import {
  Search, Plus, Trash2, Save, Globe, Tag, ChevronRight, ChevronDown, Package, Building2, ExternalLink, Check, ChevronsUpDown,
  FileText, PlayCircle, DollarSign, LifeBuoy, CheckSquare, Link2, User, Phone, Mail, MapPin, Loader2, Cloud, Layers, Shield, Star, X, Copy,
} from 'lucide-react';
import { deleteWithUndo } from '../lib/undoToast';
import MultiPlatformPicker, { PlatformBadgeStack } from './common/MultiPlatformPicker';
import LogoUpload from './LogoUpload';
import { useColumnPicker } from './common/ColumnPicker';

// D3. Saved-filter chip storage (localStorage). One key per table.
const SAVED_FILTERS_KEY = 'lodgiflow_saved_filters_products';
function loadSavedFilters() {
  try {
    const raw = localStorage.getItem(SAVED_FILTERS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function persistSavedFilters(list) {
  try { localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(list)); } catch {}
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const STATUS_OPTIONS = ['None', 'In Evaluation', 'Deployed', 'Retired', 'Not Approved'];
const STATUS_COLORS = {
  'None': 'bg-slate-50 text-slate-500 border-slate-200',
  'In Evaluation': 'bg-amber-100 text-amber-800 border-amber-200',
  'Deployed': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Retired': 'bg-slate-100 text-slate-500 border-slate-200',
  'Not Approved': 'bg-red-100 text-red-800 border-red-200',
};

export default function TechDatabaseProducts({ token, role, isSuperAdmin, navigateToProductId, onProductNavigated }) {
  const [products, setProducts] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCloud, setFilterCloud] = useState('all');
  // D3. Saved filter chips
  const [savedFilters, setSavedFilters] = useState(() => loadSavedFilters());
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [productAssignments, setProductAssignments] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [showAddAssignment, setShowAddAssignment] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [tenantStatus, setTenantStatus] = useState({});
  const [tenantStatusEdit, setTenantStatusEdit] = useState({});

  // Bulk selection
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkValue, setBulkValue] = useState('');
  const [massAssignOpen, setMassAssignOpen] = useState(false);
  const [massAssignSelected, setMassAssignSelected] = useState(new Set());
  const [massAssigning, setMassAssigning] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isAdmin = role === 'admin';

  // Decode tenant_id from token
  const tenantId = useMemo(() => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.tenant_id || '';
    } catch { return ''; }
  }, [token]);

  // Fetch property assignments for a product
  const fetchProductAssignments = useCallback(async (productId) => {
    if (!productId) return;
    try {
      const [assignRes, propRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/assignments?product_id=${productId}`, { headers }),
        axios.get(`${BACKEND_URL}/api/properties?show_all=true`, { headers }),
      ]);
      setProductAssignments(assignRes.data || []);
      setAllProperties(propRes.data || []);
    } catch { setProductAssignments([]); }
  }, [headers]);

  const addPropertyAssignment = async (propertyId) => {
    if (!selectedProduct) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/assignments`, {
        property_id: propertyId,
        platform_type_id: selectedProduct.platform_id,
        product_id: selectedProduct.id,
        vendor_name: selectedProduct.vendor_name || selectedProduct.name || '',
      }, { headers });
      toast.success('Property assigned');
      setShowAddAssignment(false);
      fetchProductAssignments(selectedProduct.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removePropertyAssignment = async (assignment) => {
    if (!selectedProduct || !window.confirm('Remove this property assignment?')) return;
    try {
      if (assignment.id) {
        // Delete by assignment ID (most reliable)
        await axios.delete(`${BACKEND_URL}/api/admin/assignments/${assignment.id}`, { headers });
      } else {
        // Fallback to compound key
        await axios.delete(`${BACKEND_URL}/api/admin/assignments`, {
          headers, params: { property_id: assignment.property_id, platform_type_id: selectedProduct.platform_id, product_id: selectedProduct.id, layer: 'actual' },
        });
      }
      toast.success('Assignment removed');
      fetchProductAssignments(selectedProduct.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const executeMassAssign = async () => {
    if (!selectedProduct || massAssignSelected.size === 0) return;
    setMassAssigning(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/assignments/bulk`, {
        property_ids: Array.from(massAssignSelected),
        platform_type_id: selectedProduct.platform_id,
        product_id: selectedProduct.id,
        vendor_name: selectedProduct.vendor_name || selectedProduct.name || '',
        layer: 'actual',
      }, { headers });
      toast.success(`Assigned to ${res.data.created} properties (${res.data.skipped} already had it)`);
      setMassAssignOpen(false);
      setMassAssignSelected(new Set());
      fetchProductAssignments(selectedProduct.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setMassAssigning(false); }
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 1000 };
      if (filterPlatform !== 'all') params.platform_id = filterPlatform;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterCloud !== 'all') params.is_cloud = filterCloud === 'cloud' ? 'true' : 'false';
      if (searchQuery) params.q = searchQuery;

      const [prodRes, platRes, catRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/products`, { headers, params }),
        axios.get(`${BACKEND_URL}/api/platform-types`, { headers }),
        axios.get(`${BACKEND_URL}/api/categories`, { headers }),
      ]);

      setProducts(prodRes.data.products);
      setTotal(prodRes.data.total);
      setPlatforms(platRes.data);
      setCategories(catRes.data);
    } catch (err) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [headers, filterPlatform, filterStatus, filterCloud, searchQuery]);

  const fetchVendorList = useCallback(async () => {
    try {
      const [vendorRes, brandRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/vendors?limit=2000`, { headers }),
        axios.get(`${BACKEND_URL}/api/brands`, { headers }),
      ]);
      setVendors(vendorRes.data.vendors);
      setBrands(brandRes.data || []);
    } catch (err) { /* ignore */ }
  }, [headers]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { fetchVendorList(); }, [fetchVendorList]);

  // Handle navigation from other tabs (e.g., platform detail "View Full Details")
  useEffect(() => {
    if (navigateToProductId && products.length > 0) {
      const product = products.find(p => p.id === navigateToProductId);
      if (product) {
        selectProduct(product);
        if (onProductNavigated) onProductNavigated();
      }
    }
  }, [navigateToProductId, products]); // eslint-disable-line

  const selectProduct = async (p) => {
    setSelectedProduct(p);
    setEditForm(p);
    setEditMode(false);
    setTenantStatus({});
    setTenantStatusEdit({});
    fetchProductAssignments(p.id);
    // Fetch full product detail with white-label data
    try {
      const detailUrl = tenantId
        ? `${BACKEND_URL}/api/products/${p.id}?tenant_id=${tenantId}`
        : `${BACKEND_URL}/api/products/${p.id}`;
      const res = await axios.get(detailUrl, { headers });
      setSelectedProduct(res.data);
      setEditForm(res.data);
      if (res.data.tenant_status) {
        setTenantStatus(res.data.tenant_status);
        setTenantStatusEdit(res.data.tenant_status);
      }
    } catch { /* use list data as fallback */ }
  };

  const saveProduct = async () => {
    try {
      // Global product data (no status fields - those are tenant-level)
      const platformIds = editForm.platform_ids && editForm.platform_ids.length > 0
        ? editForm.platform_ids
        : (editForm.platform_id ? [editForm.platform_id] : []);
      const payload = {
        name: editForm.name,
        vendor_id: editForm.vendor_id || '',
        platform_id: platformIds[0] || '',
        platform_ids: platformIds,
        description: editForm.description || '',
        website: editForm.website || '',
        documentation_url: editForm.documentation_url || '',
        demo_url: editForm.demo_url || '',
        pricing_url: editForm.pricing_url || '',
        support_url: editForm.support_url || '',
        key_features: editForm.key_features || '',
        notes: editForm.notes || '',
        tags: editForm.tags || [],
        same_as_vendor: editForm.same_as_vendor || false,
        is_cloud: editForm.is_cloud ?? true,
        sales_contact_name: editForm.sales_contact_name || '',
        sales_contact_email: editForm.sales_contact_email || '',
        sales_contact_phone: editForm.sales_contact_phone || '',
        is_white_label: editForm.is_white_label || false,
        white_label_of: editForm.white_label_of || [],
        support_sunset_date: editForm.support_sunset_date || '',
        support_sunset_note: editForm.support_sunset_note || '',
        retail_price_annual: editForm.retail_price_annual || '',
        retail_price_notes: editForm.retail_price_notes || '',
      };
      if (showNew) {
        await axios.post(`${BACKEND_URL}/api/admin/products`, payload, { headers });
        toast.success('Product created');
        setShowNew(false);
      } else {
        await axios.put(`${BACKEND_URL}/api/admin/products/${editForm.id}`, payload, { headers });
        toast.success('Product updated');
      }

      // Save tenant-level overlay if in tenant context
      if (tenantId && !showNew && editForm.id) {
        try {
          await axios.put(`${BACKEND_URL}/api/tenant-product-statuses/${editForm.id}`, {
            product_id: editForm.id,
            status: tenantStatusEdit.status || '',
            vetted: tenantStatusEdit.vetted || false,
            vetted_by: tenantStatusEdit.vetted_by || '',
            vetted_date: tenantStatusEdit.vetted_date || '',
            approved: tenantStatusEdit.approved || false,
            approved_by: tenantStatusEdit.approved_by || '',
            approved_date: tenantStatusEdit.approved_date || '',
            reviewed: tenantStatusEdit.reviewed || false,
            reviewed_by: tenantStatusEdit.reviewed_by || '',
            reviewed_date: tenantStatusEdit.reviewed_date || '',
            not_approved: tenantStatusEdit.not_approved || false,
            not_approved_by: tenantStatusEdit.not_approved_by || '',
            not_approved_date: tenantStatusEdit.not_approved_date || '',
            tenant_notes: tenantStatusEdit.tenant_notes || '',
            tenant_tags: tenantStatusEdit.tenant_tags || [],
            annual_cost: tenantStatusEdit.annual_cost || '',
            contract_value: tenantStatusEdit.contract_value || '',
            cost_per_room: tenantStatusEdit.cost_per_room || '',
            billing_frequency: tenantStatusEdit.billing_frequency || '',
            cost_notes: tenantStatusEdit.cost_notes || '',
            tenant_sales_contact_name: tenantStatusEdit.tenant_sales_contact_name || '',
            tenant_sales_contact_email: tenantStatusEdit.tenant_sales_contact_email || '',
            tenant_sales_contact_phone: tenantStatusEdit.tenant_sales_contact_phone || '',
          }, { headers });
        } catch { /* tenant overlay save is best-effort */ }
      }

      setEditMode(false);
      setEditForm({});
      setSelectedProduct(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteProduct = async (id) => {
    // Capture a snapshot BEFORE deletion so undo can restore it.
    const snapshot = selectedProduct && selectedProduct.id === id
      ? { ...selectedProduct }
      : (products.find(p => p.id === id) || null);
    const ok = await deleteWithUndo({
      confirm: 'Delete this product?',
      label: `Deleted "${snapshot?.name || 'product'}"`,
      onDelete: async () => {
        await axios.delete(`${BACKEND_URL}/api/admin/products/${id}`, { headers });
      },
      onRestore: snapshot ? async () => {
        await axios.post(`${BACKEND_URL}/api/admin/products/${id}/restore`, { snapshot }, { headers });
        fetchProducts();
      } : null,
    });
    if (ok) {
      setSelectedProduct(null);
      fetchProducts();
    }
  };

  // D4. Duplicate a product row — calls the new backend clone endpoint,
  // then opens the clone in edit mode so the user can rename it.
  const duplicateProduct = async (id) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/products/${id}/duplicate`, {}, { headers });
      toast.success(`Duplicated as "${res.data.name}" — rename it below.`);
      await fetchProducts();
      const created = res.data;
      // Auto-select the new product for editing
      try {
        const detail = await axios.get(`${BACKEND_URL}/api/admin/products/${created.id}`, { headers });
        setSelectedProduct(detail.data);
        setEditMode(true);
        setEditForm({ ...detail.data });
      } catch {}
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Duplicate failed');
    }
  };

  const toggleBulkSelect = (productId) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId); else next.add(productId);
      return next;
    });
  };

  const executeBulkAction = async () => {
    if (bulkSelected.size === 0 || !bulkAction) return;
    try {
      const payload = { product_ids: [...bulkSelected] };
      if (bulkAction === 'status') payload.status = bulkValue;
      if (bulkAction === 'platform') payload.platform_id = bulkValue;
      await axios.put(`${BACKEND_URL}/api/admin/products/bulk`, payload, { headers });
      toast.success(`Updated ${bulkSelected.size} products`);
      setBulkSelected(new Set());
      setBulkAction(null);
      setBulkValue('');
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk update failed');
    }
  };


  // Group products by category → platform (two-level hierarchy)
  const grouped = useMemo(() => {
    const catGroups = {};
    products.forEach(p => {
      const catId = p.category_id || 'uncategorized';
      const catName = p.category_name || 'Uncategorized';
      const platId = p.platform_id || 'uncategorized';
      const platName = platId === 'uncategorized' ? 'Uncategorized' : (p.platform_name || platId);

      if (!catGroups[catId]) {
        catGroups[catId] = { id: catId, name: catName, platforms: {} };
      }
      if (!catGroups[catId].platforms[platId]) {
        catGroups[catId].platforms[platId] = { id: platId, name: platName, products: [] };
      }
      catGroups[catId].platforms[platId].products.push(p);
    });

    return Object.values(catGroups)
      .map(cat => ({
        ...cat,
        platforms: Object.values(cat.platforms).map(pl => ({
          ...pl,
          products: pl.products.sort((a, b) => (a.name || '').localeCompare(b.name || '')),
        })).sort((a, b) => a.name.localeCompare(b.name)),
        productCount: Object.values(cat.platforms).reduce((sum, pl) => sum + pl.products.length, 0),
      }))
      .sort((a, b) => {
        if (a.id === 'uncategorized') return 1;
        if (b.id === 'uncategorized') return -1;
        return a.name.localeCompare(b.name);
      });
  }, [products]);

  // Default all collapsed
  const [collapsedCats, setCollapsedCats] = useState(() => {
    const initial = {};
    return initial; // all start collapsed by checking `?? true` below
  });
  const [collapsedPlatforms, setCollapsedPlatforms] = useState({});

  // Platform options grouped by category
  const platformsByCategory = useMemo(() => {
    const catMap = {};
    categories.forEach(c => { catMap[c.id] = c; });
    const groups = {};
    platforms.forEach(p => {
      const cat = catMap[p.category_id] || { name: 'Uncategorized', color: '#9CA3AF' };
      const catName = cat.name;
      if (!groups[catName]) groups[catName] = { color: cat.color || '#9CA3AF', items: [] };
      groups[catName].items.push(p);
    });
    // Sort platforms alphabetically within each group
    Object.values(groups).forEach(g => g.items.sort((a, b) => (a.name || a.short).localeCompare(b.name || b.short)));
    // Sort groups alphabetically, Uncategorized last
    return Object.entries(groups).sort((a, b) => {
      if (a[0] === 'Uncategorized') return 1;
      if (b[0] === 'Uncategorized') return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [platforms, categories]);

  const [sidebarWidth, setSidebarWidth] = useState(380);

  // Column picker for the search-mode metadata row (per-product meta chips)
  const listCols = useColumnPicker('products_list_meta', [
    { key: 'category', label: 'Category' },
    { key: 'platform', label: 'Platform' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'status', label: 'Status Badge' },
  ]);

  return (
    <div className="flex flex-col h-full overflow-hidden relative" data-testid="tech-database-products">
      {/* Toolbar */}
      <div className="px-3 md:px-5 py-3 border-b bg-card/50 flex flex-wrap items-center gap-2 md:gap-3 shrink-0">
        <div className="relative flex-1 min-w-[150px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            data-testid="products-search"
            className="pl-9 h-9"
            placeholder="Search products..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={filterPlatform} onValueChange={setFilterPlatform}>
          <SelectTrigger className="w-[160px] md:w-[200px] h-9" data-testid="products-platform-filter">
            <SelectValue placeholder="All Platforms" />
          </SelectTrigger>
          <SelectContent className="max-h-[350px]">
            <SelectItem value="all">All Platforms</SelectItem>
            {platformsByCategory.map(([catName, cat]) => (
              <SelectGroup key={catName}>
                <SelectLabel className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                  {catName}
                </SelectLabel>
                {cat.items.map(p => (
                  <SelectItem key={p.id} value={p.id} className="pl-6">{p.name || p.short}</SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px] h-9" data-testid="products-status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_OPTIONS.map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterCloud} onValueChange={setFilterCloud}>
          <SelectTrigger className="w-[140px] h-9" data-testid="products-cloud-filter">
            <Cloud className="h-3.5 w-3.5 mr-1.5 text-sky-500 shrink-0" />
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="cloud">Cloud / SaaS</SelectItem>
            <SelectItem value="premise">On-Premise</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Badge variant="secondary" className="font-mono text-xs">
          {products.length} of {total} products
        </Badge>
        <listCols.Trigger />
        {isAdmin && (
          <Button size="sm" className="gap-1.5 text-xs" onClick={() => {
            setShowNew(true);
            setEditMode(true);
            setEditForm({ name: '', platform_id: '', vendor_id: '', status: 'In Evaluation', tags: [], is_cloud: true, is_white_label: false, white_label_of: [] });
          }}>
            <Plus className="h-3.5 w-3.5" /> New Product
          </Button>
        )}
      </div>

      {/* D3. Saved-filter chip row */}
      {(savedFilters.length > 0 || filterPlatform !== 'all' || filterStatus !== 'all' || filterCloud !== 'all' || searchQuery) && (
        <div className="px-3 md:px-5 py-2 border-b bg-muted/30 flex flex-wrap items-center gap-2 text-xs" data-testid="saved-filter-chips">
          {savedFilters.map(f => (
            <button
              key={f.id}
              onClick={() => {
                setFilterPlatform(f.platform ?? 'all');
                setFilterStatus(f.status ?? 'all');
                setFilterCloud(f.cloud ?? 'all');
                setSearchQuery(f.q ?? '');
                toast.success(`Applied "${f.name}"`);
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-700 px-2.5 py-1 transition-colors group"
              data-testid={`saved-filter-${f.id}`}
            >
              <Star className="h-3 w-3 fill-violet-500 text-violet-500" />
              <span className="font-medium">{f.name}</span>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  const next = savedFilters.filter(x => x.id !== f.id);
                  setSavedFilters(next); persistSavedFilters(next);
                  toast('Filter removed');
                }}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-violet-500 hover:text-rose-500"
              >
                <X className="h-3 w-3" />
              </span>
            </button>
          ))}
          {(filterPlatform !== 'all' || filterStatus !== 'all' || filterCloud !== 'all' || searchQuery) && (
            <button
              onClick={() => {
                const name = window.prompt('Name this saved filter (e.g. "Cloud + Deployed"):');
                if (!name) return;
                const entry = {
                  id: `f_${Date.now()}`,
                  name: name.trim(),
                  q: searchQuery,
                  platform: filterPlatform,
                  status: filterStatus,
                  cloud: filterCloud,
                };
                const next = [entry, ...savedFilters.filter(f => f.name.toLowerCase() !== entry.name.toLowerCase())].slice(0, 12);
                setSavedFilters(next); persistSavedFilters(next);
                toast.success(`Saved "${entry.name}"`);
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-muted-foreground/40 text-muted-foreground hover:text-violet-700 hover:border-violet-400 px-2.5 py-1 transition-colors"
              data-testid="save-current-filter"
            >
              <Star className="h-3 w-3" />
              <span className="font-medium">Save current filter</span>
            </button>
          )}
          {(filterPlatform !== 'all' || filterStatus !== 'all' || filterCloud !== 'all' || searchQuery) && (
            <button
              onClick={() => {
                setFilterPlatform('all'); setFilterStatus('all'); setFilterCloud('all'); setSearchQuery('');
              }}
              className="text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              data-testid="clear-filters"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Content - stacked on mobile */}
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
        {/* Left: Product list - resizable */}
        <ScrollArea className={`${selectedProduct && typeof window !== 'undefined' && window.innerWidth < 768 ? 'hidden' : ''} w-full md:border-r shrink-0 ${!selectedProduct ? 'flex-1 md:flex-none' : ''}`} style={{ width: typeof window !== 'undefined' && window.innerWidth >= 768 ? `${sidebarWidth}px` : undefined }}>
          <div className="p-3 space-y-4">
            {loading ? (
              <p className="text-sm text-muted-foreground p-4 text-center">Loading...</p>
            ) : products.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">No products match your filters</p>
            ) : searchQuery ? (
              /* SEARCH MODE: flat list with category/platform details */
              <div className="space-y-0.5">
                <p className="text-xs text-muted-foreground px-1 mb-2">{products.length} result{products.length !== 1 ? 's' : ''} for "{searchQuery}"</p>
                {[...products].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(p => (
                  <div key={p.id} className="flex items-center gap-1">
                    {isAdmin && (
                      <input type="checkbox" checked={bulkSelected.has(p.id)} onChange={() => toggleBulkSelect(p.id)} className="rounded h-3.5 w-3.5 shrink-0 ml-1" onClick={e => e.stopPropagation()} />
                    )}
                    <button
                      onClick={() => selectProduct(p)}
                      className={`flex-1 text-left px-3 py-2.5 rounded-lg transition-colors ${selectedProduct?.id === p.id ? 'bg-accent ring-1 ring-ring/20' : 'hover:bg-muted/60'}`}
                    >
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {listCols.isVisible('category') && p.category_name && <span className="text-[10px] text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded">{p.category_name}</span>}
                        {/* Multi-platform indicator on list rows */}
                        {listCols.isVisible('platform') && ((p.platforms || []).length > 1 ? (
                          <PlatformBadgeStack platforms={p.platforms} size="sm" maxInline={3} testId={`product-row-${p.id}-platforms`} />
                        ) : (
                          p.platform_name && <span className="text-[10px] text-muted-foreground">{p.platform_name}</span>
                        ))}
                        {listCols.isVisible('vendor') && p.vendor_name && <span className="text-[10px] text-muted-foreground">· {p.vendor_name}</span>}
                      </div>
                    </button>
                    {listCols.isVisible('status') && (
                      <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_COLORS[p.status] || ''}`}>{p.status || '—'}</Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : grouped.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">No products match your filters</p>
            ) : (
              grouped.map(cat => {
                const isCatCollapsed = collapsedCats[cat.id] ?? (filterPlatform === 'all'); // auto-expand when filtered
                const toggleCat = () => setCollapsedCats(prev => ({ ...prev, [cat.id]: !isCatCollapsed }));
                return (
                <div key={cat.id}>
                  {/* Category header */}
                  <button onClick={toggleCat} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted/40 transition-colors mb-0.5">
                    <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform ${isCatCollapsed ? '-rotate-90' : ''}`} />
                    <span className={`text-[11px] font-bold uppercase tracking-wider truncate ${cat.id === 'uncategorized' ? 'text-amber-600' : ''}`}>{cat.name}</span>
                    <Badge variant="secondary" className="text-[10px] px-1.5 h-4 ml-auto font-mono">{cat.productCount}</Badge>
                  </button>
                  {!isCatCollapsed && (
                  <div className="ml-2 space-y-1 mb-2">
                    {cat.platforms.map(plat => {
                      const isPlatCollapsed = collapsedPlatforms[plat.id] ?? (filterPlatform === 'all'); // auto-expand when filtered
                      const togglePlat = () => setCollapsedPlatforms(prev => ({ ...prev, [plat.id]: !isPlatCollapsed }));
                      return (
                      <div key={plat.id}>
                        <button onClick={togglePlat} className="w-full flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-muted/30 transition-colors">
                          <ChevronDown className={`h-2.5 w-2.5 text-muted-foreground/60 shrink-0 transition-transform ${isPlatCollapsed ? '-rotate-90' : ''}`} />
                          <Package className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                          <span className={`text-xs font-medium text-muted-foreground truncate ${plat.id === 'uncategorized' ? 'text-amber-500' : ''}`}>{plat.name}</span>
                          <span className="text-[9px] text-muted-foreground/50 ml-auto font-mono">{plat.products.length}</span>
                        </button>
                        {!isPlatCollapsed && (
                        <div className="space-y-0.5 ml-2 mb-1">
                          {plat.products.map(p => (
                            <div key={p.id} className="flex items-center gap-1">
                              {isAdmin && (
                                <input
                                  type="checkbox"
                                  checked={bulkSelected.has(p.id)}
                                  onChange={() => toggleBulkSelect(p.id)}
                                  className="rounded h-3.5 w-3.5 shrink-0 ml-1"
                                  onClick={e => e.stopPropagation()}
                                />
                              )}
                              <button
                                onClick={() => selectProduct(p)}
                                className={`flex-1 text-left px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                                  selectedProduct?.id === p.id ? 'bg-accent ring-1 ring-ring/20' : 'hover:bg-muted/60'
                                }`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate flex items-center gap-1.5">
                                    {p.name}
                                    {p.is_white_label && <Layers className="h-3 w-3 text-orange-500 shrink-0" title="White-label" />}
                                  </div>
                                  {listCols.isVisible('vendor') && (
                                    <div className="text-xs text-muted-foreground truncate">{p.vendor_name || 'No vendor'}</div>
                                  )}
                                </div>
                                {(p.wl_child_count > 0) && (
                                  <Badge variant="outline" className="text-[9px] shrink-0 text-orange-600 border-orange-200 gap-0.5 px-1.5" title={`${p.wl_child_count} white-label products`}>
                                    WL {p.wl_child_count}
                                  </Badge>
                                )}
                                {listCols.isVisible('status') && (
                                  <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_COLORS[p.status] || ''}`}>
                                    {p.status || '—'}
                                  </Badge>
                                )}
                                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              </button>
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  )}
                </div>
                );
              })
            )}
          </div>
        </ScrollArea>

        {/* Resize handle */}
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startW = sidebarWidth;
            const move = (ev) => setSidebarWidth(Math.max(250, Math.min(Math.floor(window.innerWidth * 0.5), startW + (ev.clientX - startX))));
            const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
            document.addEventListener('mousemove', move);
            document.addEventListener('mouseup', up);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
          }}
          className="hidden md:block w-1.5 shrink-0 cursor-col-resize hover:bg-primary/20 active:bg-primary/30 transition-colors relative group"
        >
          <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-center">
            <div className="w-0.5 h-8 rounded-full bg-border group-hover:bg-primary/40 transition-colors" />
          </div>
        </div>

        {/* Right: Product detail */}
        <div className="flex-1 overflow-auto min-w-0">
          {showNew ? (
            <ScrollArea className="h-full">
              <div className="p-6 max-w-3xl space-y-5">
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>New Product</h2>
                <Separator />
                {renderForm()}
                <div className="flex gap-2 pt-2">
                  <Button onClick={saveProduct} disabled={!editForm.name || !(editForm.platform_ids && editForm.platform_ids.length > 0)} className="gap-1.5">
                    <Save className="h-3.5 w-3.5" /> Create Product
                  </Button>
                  <Button variant="ghost" onClick={() => { setShowNew(false); setEditForm({}); }}>Cancel</Button>
                </div>
              </div>
            </ScrollArea>
          ) : !selectedProduct ? (
            <div className="h-full flex items-center justify-center hidden md:flex">
              <div className="text-center">
                <Package className="h-10 w-10 text-muted-foreground/25 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Select a product to view details</p>
              </div>
            </div>
          ) : (
            <ScrollArea className="h-full">
              <div className="p-4 md:p-6 max-w-3xl space-y-5 animate-fade-in">
                {/* Mobile back button */}
                <button
                  className="md:hidden text-xs text-muted-foreground flex items-center gap-1 mb-2"
                  onClick={() => setSelectedProduct(null)}
                >
                  &larr; Back to list
                </button>
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="outline" className={`text-xs mb-1 ${STATUS_COLORS[tenantStatus.status || selectedProduct.status] || ''}`}>
                      {tenantStatus.status || selectedProduct.status || 'Unknown'}
                    </Badge>
                    {selectedProduct.support_sunset_date && (
                      <Badge variant="outline" className="text-xs mb-1 ml-1 text-amber-700 border-amber-300 bg-amber-50">
                        Sunset: {selectedProduct.support_sunset_date}
                      </Badge>
                    )}
                    <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {selectedProduct.name}
                    </h2>
                    {/* Multi-platform badge stack — shows every platform this product covers */}
                    {(selectedProduct.platforms || []).length > 0 ? (
                      <PlatformBadgeStack
                        platforms={selectedProduct.platforms}
                        className="mt-2"
                        testId="product-detail-platforms"
                        maxInline={6}
                      />
                    ) : (
                      <div className="mt-2 text-xs text-muted-foreground">{selectedProduct.platform_name || selectedProduct.platform_id}</div>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      {selectedProduct.vendor_name && (
                        <span className="text-sm text-muted-foreground">Vendor: <strong>{selectedProduct.vendor_name}</strong></span>
                      )}
                      {selectedProduct.same_as_vendor && (
                        <Badge variant="outline" className="text-[9px] text-blue-600 border-blue-200">Same as Vendor</Badge>
                      )}
                      {(selectedProduct.is_cloud !== false) && (
                        <Badge variant="outline" className="text-[9px] text-sky-600 border-sky-200 gap-0.5">
                          <Cloud className="h-2.5 w-2.5" /> Cloud
                        </Badge>
                      )}
                      {selectedProduct.is_cloud === false && (
                        <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-200">On-Premise</Badge>
                      )}
                      {(tenantStatus.vetted || selectedProduct.vetted) && (
                        <Badge className="text-[9px] bg-blue-100 text-blue-700 border-blue-200">Vetted</Badge>
                      )}
                      {(tenantStatus.reviewed || selectedProduct.reviewed) && (
                        <Badge className="text-[9px] bg-purple-100 text-purple-700 border-purple-200">Reviewed</Badge>
                      )}
                      {(tenantStatus.approved || selectedProduct.approved) && (
                        <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200">Approved</Badge>
                      )}
                      {(tenantStatus.not_approved || selectedProduct.not_approved) && (
                        <Badge className="text-[9px] bg-red-100 text-red-700 border-red-200">Not Approved / Sunset</Badge>
                      )}
                      {selectedProduct.is_white_label && (
                        <Badge variant="outline" className="text-[9px] text-orange-600 border-orange-200 gap-0.5">
                          <Layers className="h-2.5 w-2.5" /> White-label
                        </Badge>
                      )}
                      {(selectedProduct.white_label_children || []).length > 0 && (
                        <Badge variant="outline" className="text-[9px] text-orange-600 border-orange-200 gap-0.5 font-mono">
                          <Layers className="h-2.5 w-2.5" /> WL {selectedProduct.white_label_children.length}
                        </Badge>
                      )}
                    </div>
                    {/* Status details - from tenant overlay */}
                    {(tenantStatus.reviewed || tenantStatus.vetted || tenantStatus.approved || tenantStatus.not_approved) && (
                      <div className="flex flex-wrap gap-4 mt-2">
                        {tenantStatus.reviewed && (
                          <div className="text-xs text-muted-foreground">
                            Reviewed{tenantStatus.reviewed_by ? ` by ${tenantStatus.reviewed_by}` : ''}{tenantStatus.reviewed_date ? ` on ${tenantStatus.reviewed_date}` : ''}
                          </div>
                        )}
                        {tenantStatus.vetted && (
                          <div className="text-xs text-muted-foreground">
                            Vetted{tenantStatus.vetted_by ? ` by ${tenantStatus.vetted_by}` : ''}{tenantStatus.vetted_date ? ` on ${tenantStatus.vetted_date}` : ''}
                          </div>
                        )}
                        {tenantStatus.approved && (
                          <div className="text-xs text-muted-foreground">
                            Approved{tenantStatus.approved_by ? ` by ${tenantStatus.approved_by}` : ''}{tenantStatus.approved_date ? ` on ${tenantStatus.approved_date}` : ''}
                          </div>
                        )}
                        {tenantStatus.not_approved && (
                          <div className="text-xs text-red-600">
                            Not Approved{tenantStatus.not_approved_by ? ` by ${tenantStatus.not_approved_by}` : ''}{tenantStatus.not_approved_date ? ` on ${tenantStatus.not_approved_date}` : ''}
                          </div>
                        )}
                      </div>
                    )}
                    {selectedProduct.support_sunset_note && !editMode && (
                      <div className="text-xs text-amber-700 mt-1">
                        Vendor Advisory: {selectedProduct.support_sunset_note}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {isAdmin && !editMode && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => { setEditMode(true); setEditForm(selectedProduct); }}>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => duplicateProduct(selectedProduct.id)} title="Duplicate & rename" data-testid="product-duplicate-btn">
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteProduct(selectedProduct.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    {editMode && !showNew && (
                      <>
                        <Button size="sm" onClick={saveProduct} className="gap-1"><Save className="h-3.5 w-3.5" /> Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditMode(false); setEditForm(selectedProduct); }}>Cancel</Button>
                      </>
                    )}
                  </div>
                </div>

                <Separator />

                {/* White-label indicator (read-only) */}
                {!editMode && selectedProduct.is_white_label && (selectedProduct.white_label_parents || []).length > 0 && (
                  <div className="p-3 rounded-lg border border-orange-200 bg-orange-50/50">
                    <h3 className="text-xs font-semibold text-orange-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                      <Layers className="h-3.5 w-3.5" /> White-label of
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedProduct.white_label_parents.map(wp => (
                        <button key={wp.id} onClick={() => selectProduct(wp)} className="text-xs text-orange-700 bg-orange-100 hover:bg-orange-200 px-2 py-1 rounded-md transition-colors">
                          {wp.name} <span className="text-orange-500">({wp.platform_name})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Product footprint — shows for every product; expands to white-label breakdown if this product has children */}
                {!editMode && (() => {
                  const wlChildren = selectedProduct.white_label_children || [];
                  const wlCount = selectedProduct.white_label_children_assignment_total || 0;
                  const direct = productAssignments.length;
                  const total = direct + wlCount;
                  const hasChildren = wlChildren.length > 0;
                  return (
                    <div className={`p-3 rounded-lg border ${hasChildren ? 'border-orange-200 bg-orange-50/50' : 'border-slate-200 bg-slate-50/60'}`} data-testid="product-footprint-box">
                      <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1.5 flex items-center gap-1.5 ${hasChildren ? 'text-orange-700' : 'text-slate-700'}`}>
                        <Layers className="h-3.5 w-3.5" /> Product Footprint
                      </h3>
                      <div className="flex items-baseline gap-3 mb-2">
                        <span className={`text-lg font-bold ${hasChildren ? 'text-orange-800' : 'text-slate-800'}`} data-testid="product-footprint-total">
                          {total}
                        </span>
                        <span className={`text-xs ${hasChildren ? 'text-orange-600' : 'text-slate-500'}`}>
                          {hasChildren ? (
                            <>total propert{total !== 1 ? 'ies' : 'y'} ({direct} direct + {wlCount} via {wlChildren.length} white-label{wlChildren.length !== 1 ? 's' : ''})</>
                          ) : (
                            <>propert{direct !== 1 ? 'ies' : 'y'} with this product deployed</>
                          )}
                        </span>
                      </div>
                      {hasChildren && (
                        <div className="space-y-1">
                          {wlChildren.map(wlc => (
                            <button key={wlc.id} onClick={() => selectProduct(wlc)}
                              className="w-full text-left flex items-center gap-2 p-2 rounded-md bg-white/60 hover:bg-white text-xs transition-colors group">
                              <Layers className="h-3 w-3 text-orange-400 shrink-0" />
                              <span className="font-medium flex-1 truncate">{wlc.name}</span>
                              <span className="text-muted-foreground text-[10px]">{wlc.platform_name}</span>
                              <Badge variant="outline" className="text-[9px] text-orange-600 border-orange-200">
                                {wlc.assignment_count} prop{wlc.assignment_count !== 1 ? 's' : ''}
                              </Badge>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {editMode ? renderForm() : (
                  <>
                    {selectedProduct.description && (
                      <div>
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Description</h3>
                        <p className="text-sm text-foreground/80">{selectedProduct.description}</p>
                      </div>
                    )}
                    {selectedProduct.key_features && (
                      <div>
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Key Features</h3>
                        <p className="text-sm text-foreground/80 whitespace-pre-wrap">{selectedProduct.key_features}</p>
                      </div>
                    )}

                    {/* Links */}
                    <div>
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Links</h3>
                      <div className="space-y-1.5">
                        {selectedProduct.website && (
                          <a href={selectedProduct.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline truncate">
                            <Globe className="h-3.5 w-3.5 shrink-0" /> {selectedProduct.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                          </a>
                        )}
                        {selectedProduct.documentation_url && (
                          <a href={selectedProduct.documentation_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                            <FileText className="h-3.5 w-3.5 shrink-0" /> Documentation
                          </a>
                        )}
                        {selectedProduct.demo_url && (
                          <a href={selectedProduct.demo_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                            <PlayCircle className="h-3.5 w-3.5 shrink-0" /> Demo / Trial
                          </a>
                        )}
                        {selectedProduct.pricing_url && (
                          <a href={selectedProduct.pricing_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                            <DollarSign className="h-3.5 w-3.5 shrink-0" /> Pricing
                          </a>
                        )}
                        {selectedProduct.support_url && (
                          <a href={selectedProduct.support_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                            <LifeBuoy className="h-3.5 w-3.5 shrink-0" /> Support
                          </a>
                        )}
                        {!selectedProduct.website && !selectedProduct.documentation_url && !selectedProduct.demo_url && !selectedProduct.pricing_url && !selectedProduct.support_url && (
                          <p className="text-sm text-muted-foreground">No links added yet.</p>
                        )}
                      </div>
                    </div>

                    {selectedProduct.notes && (
                      <div>
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Notes</h3>
                        <p className="text-sm text-foreground/80">{selectedProduct.notes}</p>
                      </div>
                    )}

                    {/* Sales Contact */}
                    {(selectedProduct.sales_contact_name || selectedProduct.sales_contact_email || selectedProduct.sales_contact_phone) && (
                      <div>
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Sales Contact</h3>
                        <div className="space-y-1.5">
                          {selectedProduct.sales_contact_name && <div className="flex items-center gap-2 text-sm"><User className="h-3.5 w-3.5 text-muted-foreground" /> {selectedProduct.sales_contact_name}</div>}
                          {selectedProduct.sales_contact_email && <a href={`mailto:${selectedProduct.sales_contact_email}`} className="flex items-center gap-2 text-sm text-blue-600 hover:underline"><Mail className="h-3.5 w-3.5" /> {selectedProduct.sales_contact_email}</a>}
                          {selectedProduct.sales_contact_phone && <a href={`tel:${selectedProduct.sales_contact_phone}`} className="flex items-center gap-2 text-sm text-blue-600 hover:underline"><Phone className="h-3.5 w-3.5" /> {selectedProduct.sales_contact_phone}</a>}
                        </div>
                      </div>
                    )}
                    {(selectedProduct.tags || []).length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Tags</h3>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedProduct.tags.map((t, i) => (
                            <Badge key={i} variant="secondary" className="text-xs"><Tag className="h-3 w-3 mr-1" />{t}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Brand Designations (read-only) */}
                    {(selectedProduct.brand_designations || []).length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Brand Designations</h3>
                        <div className="space-y-1.5">
                          {selectedProduct.brand_designations.map((bd, i) => (
                            <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 text-xs">
                              <Shield className="h-3.5 w-3.5 shrink-0" style={{ color: bd.brand_color || '#6366F1' }} />
                              <span className="font-medium">{bd.brand_name}</span>
                              <div className="flex gap-1.5 ml-auto">
                                {bd.required && <Badge className="text-[8px] bg-red-100 text-red-700">Required</Badge>}
                                {bd.preferred && <Badge className="text-[8px] bg-blue-100 text-blue-700">Preferred</Badge>}
                                {bd.approved && <Badge className="text-[8px] bg-emerald-100 text-emerald-700">Approved</Badge>}
                                {bd.grandfathered && <Badge className="text-[8px] bg-amber-100 text-amber-700">Grandfathered</Badge>}
                                {!bd.required && !bd.preferred && !bd.approved && !bd.grandfathered && bd.designation && (
                                  <Badge variant="outline" className="text-[8px]">{bd.designation}</Badge>
                                )}
                              </div>
                              {bd.date && <span className="text-[10px] text-muted-foreground">{bd.date}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tenant-specific data (read-only view) */}
                    {tenantId && (tenantStatus.tenant_notes || (tenantStatus.tenant_tags || []).length > 0 || tenantStatus.annual_cost || tenantStatus.tenant_sales_contact_name) && (
                      <div className="space-y-3 p-3 rounded-lg border border-indigo-200 bg-indigo-50/30">
                        <h3 className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">Your Organization's Data</h3>
                        {tenantStatus.tenant_notes && (
                          <div>
                            <p className="text-[10px] text-muted-foreground uppercase mb-0.5">Notes</p>
                            <p className="text-sm">{tenantStatus.tenant_notes}</p>
                          </div>
                        )}
                        {(tenantStatus.tenant_tags || []).length > 0 && (
                          <div>
                            <p className="text-[10px] text-muted-foreground uppercase mb-0.5">Tags</p>
                            <div className="flex flex-wrap gap-1">
                              {tenantStatus.tenant_tags.map((t, i) => (
                                <Badge key={i} variant="outline" className="text-xs text-indigo-700 border-indigo-200">{t}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {(tenantStatus.annual_cost || tenantStatus.contract_value || tenantStatus.cost_per_room) && (
                          <div>
                            <p className="text-[10px] text-muted-foreground uppercase mb-0.5">Cost</p>
                            <div className="flex gap-4 text-sm">
                              {tenantStatus.annual_cost && <span>Annual: <strong>{tenantStatus.annual_cost}</strong></span>}
                              {tenantStatus.contract_value && <span>Contract: <strong>{tenantStatus.contract_value}</strong></span>}
                              {tenantStatus.cost_per_room && <span>Per Room: <strong>{tenantStatus.cost_per_room}</strong></span>}
                              {tenantStatus.billing_frequency && <span className="text-muted-foreground">({tenantStatus.billing_frequency})</span>}
                            </div>
                            {tenantStatus.cost_notes && <p className="text-xs text-muted-foreground mt-0.5">{tenantStatus.cost_notes}</p>}
                          </div>
                        )}
                        {tenantStatus.tenant_sales_contact_name && (
                          <div>
                            <p className="text-[10px] text-muted-foreground uppercase mb-0.5">Your Sales Contact</p>
                            <div className="flex gap-3 text-sm">
                              <span>{tenantStatus.tenant_sales_contact_name}</span>
                              {tenantStatus.tenant_sales_contact_email && <a href={`mailto:${tenantStatus.tenant_sales_contact_email}`} className="text-blue-600 hover:underline">{tenantStatus.tenant_sales_contact_email}</a>}
                              {tenantStatus.tenant_sales_contact_phone && <span className="text-muted-foreground">{tenantStatus.tenant_sales_contact_phone}</span>}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Retail pricing (global) */}
                    {(selectedProduct.retail_price_annual || selectedProduct.retail_price_notes) && (
                      <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/30">
                        <h3 className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-1">Vendor Retail Pricing</h3>
                        <div className="flex gap-4 text-sm">
                          {selectedProduct.retail_price_annual && <span>List Price: <strong>{selectedProduct.retail_price_annual}</strong></span>}
                          {selectedProduct.retail_price_notes && <span className="text-muted-foreground">{selectedProduct.retail_price_notes}</span>}
                        </div>
                      </div>
                    )}

                    {/* Property Assignments */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Property Assignments ({productAssignments.length})
                        </h3>
                        {isAdmin && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="text-xs gap-1 h-6" onClick={() => setShowAddAssignment(true)} data-testid="add-property-assignment">
                              <Plus className="h-3 w-3" /> Add
                            </Button>
                            <Button size="sm" variant="outline" className="text-xs gap-1 h-6" onClick={() => { setMassAssignOpen(true); setMassAssignSelected(new Set()); }} data-testid="mass-assign-btn">
                              <Building2 className="h-3 w-3" /> Mass Assign
                            </Button>
                          </div>
                        )}
                      </div>
                      {productAssignments.length === 0 ? (
                        <p className="text-xs text-muted-foreground">Not deployed at any property.</p>
                      ) : (
                        <div className="space-y-1">
                          {productAssignments.map((a, i) => {
                            const prop = allProperties.find(p => p.id === a.property_id);
                            return (
                              <div key={a.id || i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/40 text-xs group">
                                <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
                                <span className="font-medium flex-1">{prop?.name || a.property_id}</span>
                                <span className="text-muted-foreground text-[10px] truncate max-w-[100px]">{a.product_name || a.vendor_name || ''}</span>
                                <Badge variant="outline" className="text-[8px]">{prop?.type || ''}</Badge>
                                {isAdmin && (
                                  <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 shrink-0"
                                    onClick={() => removePropertyAssignment(a)} data-testid={`remove-assign-${a.property_id}`}>
                                    <Trash2 className="h-3 w-3 text-destructive" />
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {/* Add assignment dropdown */}
                      {showAddAssignment && (
                        <div className="mt-2 border rounded-lg p-2 bg-card space-y-1 max-h-[200px] overflow-y-auto">
                          <p className="text-[10px] text-muted-foreground font-semibold uppercase mb-1">Select property to assign</p>
                          {allProperties
                            .filter(p => !productAssignments.some(a => a.property_id === p.id))
                            .map(p => (
                              <button key={p.id} onClick={() => addPropertyAssignment(p.id)}
                                className="w-full text-left p-2 rounded-md text-xs hover:bg-muted/60 flex items-center gap-2">
                                <MapPin className="h-3 w-3 text-muted-foreground" />
                                <span className="font-medium">{p.name}</span>
                                <Badge variant="outline" className="text-[8px] ml-auto">{p.type}</Badge>
                              </button>
                            ))}
                          <Button size="sm" variant="ghost" className="w-full text-xs mt-1" onClick={() => setShowAddAssignment(false)}>Cancel</Button>
                        </div>
                      )}

                      {/* Mass Assign Dialog */}
                      {massAssignOpen && (
                        <div className="mt-2 border rounded-lg p-3 bg-card space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold">Mass Assign: {selectedProduct?.name}</p>
                            <span className="text-[10px] text-muted-foreground">{massAssignSelected.size} selected</span>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="ghost" className="text-[10px] h-6" onClick={() => {
                              const unassigned = allProperties.filter(p => !productAssignments.some(a => a.property_id === p.id));
                              setMassAssignSelected(new Set(unassigned.map(p => p.id)));
                            }}>Select All Unassigned</Button>
                            <Button size="sm" variant="ghost" className="text-[10px] h-6" onClick={() => setMassAssignSelected(new Set())}>Clear</Button>
                          </div>
                          <div className="max-h-[250px] overflow-y-auto space-y-0.5">
                            {allProperties.map(p => {
                              const alreadyAssigned = productAssignments.some(a => a.property_id === p.id);
                              const isSelected = massAssignSelected.has(p.id);
                              return (
                                <label key={p.id} className={`flex items-center gap-2 p-2 rounded-md text-xs cursor-pointer ${alreadyAssigned ? 'opacity-40' : isSelected ? 'bg-primary/5 ring-1 ring-primary/20' : 'hover:bg-muted/60'}`}>
                                  <Checkbox
                                    checked={isSelected || alreadyAssigned}
                                    disabled={alreadyAssigned}
                                    onCheckedChange={() => {
                                      if (alreadyAssigned) return;
                                      const next = new Set(massAssignSelected);
                                      if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
                                      setMassAssignSelected(next);
                                    }}
                                  />
                                  <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
                                  <span className="font-medium flex-1">{p.name}</span>
                                  <Badge variant="outline" className="text-[8px]">{p.type}</Badge>
                                  {alreadyAssigned && <span className="text-[8px] text-emerald-600">Assigned</span>}
                                </label>
                              );
                            })}
                          </div>
                          <div className="flex gap-2 pt-1 border-t">
                            <Button size="sm" variant="ghost" className="flex-1 text-xs" onClick={() => setMassAssignOpen(false)}>Cancel</Button>
                            <Button size="sm" className="flex-1 text-xs gap-1" onClick={executeMassAssign} disabled={massAssigning || massAssignSelected.size === 0} data-testid="execute-mass-assign">
                              {massAssigning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Building2 className="h-3 w-3" />}
                              Assign to {massAssignSelected.size} Properties
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {isAdmin && bulkSelected.size > 0 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 px-4 py-2.5 rounded-xl border bg-card/95 backdrop-blur shadow-xl" data-testid="bulk-action-bar">
          <CheckSquare className="h-4 w-4 text-primary shrink-0" />
          <span className="text-xs font-medium">{bulkSelected.size} selected</span>
          <Select value={bulkAction || ''} onValueChange={v => { setBulkAction(v); setBulkValue(''); }}>
            <SelectTrigger className="h-7 w-32 text-xs"><SelectValue placeholder="Action..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="status">Change Status</SelectItem>
              <SelectItem value="platform">Move Platform</SelectItem>
            </SelectContent>
          </Select>
          {bulkAction === 'status' && (
            <Select value={bulkValue} onValueChange={setBulkValue}>
              <SelectTrigger className="h-7 w-32 text-xs"><SelectValue placeholder="Status..." /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {bulkAction === 'platform' && (
            <Select value={bulkValue} onValueChange={setBulkValue}>
              <SelectTrigger className="h-7 w-36 text-xs"><SelectValue placeholder="Platform..." /></SelectTrigger>
              <SelectContent>
                {platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name || p.short}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button size="sm" className="h-7 text-xs" disabled={!bulkAction || !bulkValue} onClick={executeBulkAction} data-testid="bulk-execute">
            Apply
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setBulkSelected(new Set()); setBulkAction(null); }}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );

  function renderForm() {
    return (
      <div className="space-y-4">
        {/* Product logo uploader — mirrors the vendor/property upload UX */}
        {selectedProduct?.id && !showNew && (
          <div className="flex items-start gap-4 p-3 rounded-lg border bg-muted/20" data-testid="product-logo-editor">
            <LogoUpload
              entityType="product"
              entityId={selectedProduct.id}
              logoPath={selectedProduct.logo_path || editForm.logo_path}
              token={token}
              onUpdate={() => selectProduct(selectedProduct)}
              size="md"
            />
            <div className="text-xs text-muted-foreground pt-1">
              <div className="font-medium text-foreground mb-0.5">Product logo</div>
              PNG, JPG, SVG, or WebP up to 5&nbsp;MB.<br />White borders are auto-stripped to transparency.
            </div>
          </div>
        )}
        <div>
          <Label className="text-xs">Product Name</Label>
          <Input value={editForm.name || ''} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} placeholder="Product name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Platforms <span className="text-muted-foreground font-normal">(one or more)</span></Label>
            <MultiPlatformPicker
              value={editForm.platform_ids && editForm.platform_ids.length > 0
                ? editForm.platform_ids
                : (editForm.platform_id ? [editForm.platform_id] : [])}
              platformsByCategory={platformsByCategory}
              onChange={(next) => setEditForm(p => ({
                ...p,
                platform_ids: next,
                // Keep single platform_id in sync as first-of-list for backwards-compat consumers
                platform_id: next[0] || '',
              }))}
              testId="product-edit-platforms"
            />
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={editForm.status || 'In Evaluation'} onValueChange={v => setEditForm(p => ({ ...p, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs">Vendor</Label>
          <VendorCombobox
            vendors={vendors}
            value={editForm.vendor_id || ''}
            onChange={(v) => setEditForm(p => ({ ...p, vendor_id: v }))}
          />
        </div>
        <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/20">
          <Checkbox
            id="same-as-vendor"
            checked={editForm.same_as_vendor || false}
            onCheckedChange={(v) => {
              const checked = !!v;
              setEditForm(p => {
                const updated = { ...p, same_as_vendor: checked };
                if (checked && p.vendor_id) {
                  const vendor = vendors.find(vn => vn.id === p.vendor_id);
                  if (vendor) {
                    updated.description = vendor.description || p.description || '';
                    updated.website = vendor.website || p.website || '';
                    updated.tags = (vendor.tags && vendor.tags.length > 0) ? vendor.tags : (p.tags || []);
                    toast.success(`Populated details from ${vendor.name}`);
                  }
                }
                return updated;
              });
            }}
          />
          <label htmlFor="same-as-vendor" className="text-xs cursor-pointer"><span className="font-medium">Same as vendor</span> — inherit description, website, and tags from the vendor record</label>
        </div>
        <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/20">
          <Checkbox
            id="is-cloud"
            checked={editForm.is_cloud ?? true}
            onCheckedChange={(v) => setEditForm(p => ({ ...p, is_cloud: !!v }))}
            data-testid="product-is-cloud"
          />
          <label htmlFor="is-cloud" className="text-xs cursor-pointer flex items-center gap-1.5">
            <Cloud className="h-3.5 w-3.5 text-sky-500" />
            <span className="font-medium">Cloud</span> — this product is cloud/SaaS-based
          </label>
        </div>
        {/* White-label */}
        <div className="space-y-2 p-3 rounded-lg border bg-muted/20">
          <div className="flex items-center gap-2">
            <Checkbox
              id="is-white-label"
              checked={editForm.is_white_label || false}
              onCheckedChange={(v) => setEditForm(p => ({ ...p, is_white_label: !!v, white_label_of: !!v ? (p.white_label_of || []) : [] }))}
              data-testid="product-is-white-label"
            />
            <label htmlFor="is-white-label" className="text-xs cursor-pointer flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5 text-orange-500" />
              <span className="font-medium">White-label</span> — this product is a white-label of another product
            </label>
          </div>
          {editForm.is_white_label && (
            <div className="ml-6 space-y-2">
              <Label className="text-xs text-muted-foreground">White-label of (select parent products):</Label>
              <ProductMultiSelect
                products={products}
                platformsByCategory={platformsByCategory}
                platforms={platforms}
                categories={categories}
                value={editForm.white_label_of || []}
                excludeId={editForm.id}
                onChange={(ids) => setEditForm(p => ({ ...p, white_label_of: ids }))}
              />
            </div>
          )}
        </div>
        {/* Brand Designations - Grouped by Parent Company */}
        {!showNew && editForm.id && (
        <BrandDesignationEditor
          designations={editForm.brand_designations || []}
          brands={brands}
          productId={editForm.id}
          headers={headers}
          onUpdate={(newDesignations) => setEditForm(p => ({ ...p, brand_designations: newDesignations }))}
        />
        )}
        <div>
          <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Vendor Sales Contact (Global)</Label>
          <div className="space-y-2 mt-1.5">
            <div className="flex items-center gap-2">
              <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <Input value={editForm.sales_contact_name || ''} onChange={e => setEditForm(p => ({ ...p, sales_contact_name: e.target.value }))} placeholder="Contact name" />
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <Input value={editForm.sales_contact_email || ''} onChange={e => setEditForm(p => ({ ...p, sales_contact_email: e.target.value }))} placeholder="sales@vendor.com" />
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <Input value={editForm.sales_contact_phone || ''} onChange={e => setEditForm(p => ({ ...p, sales_contact_phone: e.target.value }))} placeholder="+1 (555) 123-4567" />
            </div>
          </div>
        </div>
        {/* Support Sunset (Global - visible to all, editable by super admins) */}
        <div className="space-y-2 p-3 rounded-lg border border-amber-200 bg-amber-50/30">
          <Label className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Support Sunset (Global Advisory)</Label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Sunset Date</Label>
              <Input type="date" value={editForm.support_sunset_date || ''} onChange={e => setEditForm(p => ({ ...p, support_sunset_date: e.target.value }))} className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Sunset Note</Label>
              <Input value={editForm.support_sunset_note || ''} onChange={e => setEditForm(p => ({ ...p, support_sunset_note: e.target.value }))} placeholder="e.g., EOL announced Q2 2027" className="h-8 text-xs" />
            </div>
          </div>
        </div>
        {/* Tenant-Level Vetting & Approval (only shown when in tenant context) */}
        {tenantId && (
        <div className="space-y-3 p-3 rounded-lg border border-indigo-200 bg-indigo-50/30">
          <Label className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">Your Organization's Review Status</Label>
          <div className="mb-2">
            <Label className="text-[10px] text-muted-foreground">Status</Label>
            <Select value={tenantStatusEdit.status || ''} onValueChange={v => setTenantStatusEdit(p => ({ ...p, status: v }))}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Set status..." /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox id="t-reviewed" checked={tenantStatusEdit.reviewed || false} onCheckedChange={v => setTenantStatusEdit(p => ({ ...p, reviewed: !!v, reviewed_date: !!v && !p.reviewed_date ? new Date().toISOString().split('T')[0] : p.reviewed_date }))} />
                <label htmlFor="t-reviewed" className="text-sm font-medium cursor-pointer">Reviewed</label>
              </div>
              {tenantStatusEdit.reviewed && (
                <div className="ml-6 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <Input value={tenantStatusEdit.reviewed_by || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, reviewed_by: e.target.value }))} placeholder="Reviewed by" className="h-8 text-xs" />
                  </div>
                  <Input type="date" value={tenantStatusEdit.reviewed_date || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, reviewed_date: e.target.value }))} className="h-8 text-xs ml-6" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox id="t-vetted" checked={tenantStatusEdit.vetted || false} onCheckedChange={v => setTenantStatusEdit(p => ({ ...p, vetted: !!v, vetted_date: !!v && !p.vetted_date ? new Date().toISOString().split('T')[0] : p.vetted_date }))} />
                <label htmlFor="t-vetted" className="text-sm font-medium cursor-pointer">Vetted</label>
              </div>
              {tenantStatusEdit.vetted && (
                <div className="ml-6 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <Input value={tenantStatusEdit.vetted_by || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, vetted_by: e.target.value }))} placeholder="Vetted by" className="h-8 text-xs" />
                  </div>
                  <Input type="date" value={tenantStatusEdit.vetted_date || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, vetted_date: e.target.value }))} className="h-8 text-xs ml-6" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox id="t-approved" checked={tenantStatusEdit.approved || false} onCheckedChange={v => setTenantStatusEdit(p => ({ ...p, approved: !!v, approved_date: !!v && !p.approved_date ? new Date().toISOString().split('T')[0] : p.approved_date }))} />
                <label htmlFor="t-approved" className="text-sm font-medium cursor-pointer">Approved</label>
              </div>
              {tenantStatusEdit.approved && (
                <div className="ml-6 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <Input value={tenantStatusEdit.approved_by || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, approved_by: e.target.value }))} placeholder="Approved by" className="h-8 text-xs" />
                  </div>
                  <Input type="date" value={tenantStatusEdit.approved_date || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, approved_date: e.target.value }))} className="h-8 text-xs ml-6" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox id="t-not-approved" checked={tenantStatusEdit.not_approved || false} onCheckedChange={v => setTenantStatusEdit(p => ({ ...p, not_approved: !!v, not_approved_date: !!v && !p.not_approved_date ? new Date().toISOString().split('T')[0] : p.not_approved_date }))} />
                <label htmlFor="t-not-approved" className="text-sm font-medium cursor-pointer text-red-700">Not Approved / Sunset</label>
              </div>
              {tenantStatusEdit.not_approved && (
                <div className="ml-6 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <Input value={tenantStatusEdit.not_approved_by || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, not_approved_by: e.target.value }))} placeholder="Decided by" className="h-8 text-xs" />
                  </div>
                  <Input type="date" value={tenantStatusEdit.not_approved_date || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, not_approved_date: e.target.value }))} className="h-8 text-xs ml-6" />
                </div>
              )}
            </div>
          </div>
        </div>
        )}
        {/* Tenant-specific Notes */}
        {tenantId && (
        <div className="space-y-2 p-3 rounded-lg border border-indigo-200 bg-indigo-50/30">
          <Label className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">Your Organization's Notes</Label>
          <Textarea value={tenantStatusEdit.tenant_notes || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, tenant_notes: e.target.value }))} rows={2} placeholder="Internal notes about this product (private to your organization)..." className="text-xs" />
        </div>
        )}
        {/* Tenant-specific Tags */}
        {tenantId && (
        <div className="space-y-2 p-3 rounded-lg border border-indigo-200 bg-indigo-50/30">
          <Label className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">Your Organization's Tags</Label>
          <TagInput value={tenantStatusEdit.tenant_tags || []} onChange={tags => setTenantStatusEdit(p => ({ ...p, tenant_tags: tags }))} token={token} />
          <p className="text-[10px] text-muted-foreground">Private tags visible only to your organization (e.g., "PCI-scope", "migration-candidate")</p>
        </div>
        )}
        {/* Tenant-specific Cost Data */}
        {tenantId && (
        <div className="space-y-2 p-3 rounded-lg border border-indigo-200 bg-indigo-50/30">
          <Label className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">Your Organization's Cost Data</Label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Annual Cost</Label>
              <Input value={tenantStatusEdit.annual_cost || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, annual_cost: e.target.value }))} placeholder="$12,000" className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Contract Value</Label>
              <Input value={tenantStatusEdit.contract_value || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, contract_value: e.target.value }))} placeholder="$36,000 (3yr)" className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Cost Per Room/Unit</Label>
              <Input value={tenantStatusEdit.cost_per_room || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, cost_per_room: e.target.value }))} placeholder="$5/room/month" className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Billing Frequency</Label>
              <Select value={tenantStatusEdit.billing_frequency || ''} onValueChange={v => setTenantStatusEdit(p => ({ ...p, billing_frequency: v }))}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                  <SelectItem value="one-time">One-time</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Input value={tenantStatusEdit.cost_notes || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, cost_notes: e.target.value }))} placeholder="Cost notes (e.g., negotiated rate, includes support)" className="h-8 text-xs" />
        </div>
        )}
        {/* Tenant-specific Sales Contact */}
        {tenantId && (
        <div className="space-y-2 p-3 rounded-lg border border-indigo-200 bg-indigo-50/30">
          <Label className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">Your Organization's Sales Contact</Label>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User className="h-3.5 w-3.5 text-indigo-400 shrink-0" />
              <Input value={tenantStatusEdit.tenant_sales_contact_name || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, tenant_sales_contact_name: e.target.value }))} placeholder="Your rep's name" className="h-8 text-xs" />
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-3.5 w-3.5 text-indigo-400 shrink-0" />
              <Input value={tenantStatusEdit.tenant_sales_contact_email || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, tenant_sales_contact_email: e.target.value }))} placeholder="your.rep@vendor.com" className="h-8 text-xs" />
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-3.5 w-3.5 text-indigo-400 shrink-0" />
              <Input value={tenantStatusEdit.tenant_sales_contact_phone || ''} onChange={e => setTenantStatusEdit(p => ({ ...p, tenant_sales_contact_phone: e.target.value }))} placeholder="+1 (555) 123-4567" className="h-8 text-xs" />
            </div>
          </div>
        </div>
        )}
        {/* Retail Pricing (Global - vendor-provided rates) */}
        <div className="space-y-2 p-3 rounded-lg border border-emerald-200 bg-emerald-50/30">
          <Label className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Retail Pricing (Vendor Published Rates)</Label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Annual List Price</Label>
              <Input value={editForm.retail_price_annual || ''} onChange={e => setEditForm(p => ({ ...p, retail_price_annual: e.target.value }))} placeholder="$X/room/month or $X/year" className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Pricing Notes</Label>
              <Input value={editForm.retail_price_notes || ''} onChange={e => setEditForm(p => ({ ...p, retail_price_notes: e.target.value }))} placeholder="e.g., Tiered by room count" className="h-8 text-xs" />
            </div>
          </div>
        </div>
        <div>
          <Textarea value={editForm.description || ''} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} rows={3} placeholder="Product description..." />
        </div>
        <div>
          <Label className="text-xs">Key Features</Label>
          <Textarea value={editForm.key_features || ''} onChange={e => setEditForm(p => ({ ...p, key_features: e.target.value }))} rows={3} placeholder="Key features..." />
        </div>
        <div>
          <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Links</Label>
          <div className="space-y-2 mt-1.5">
            <div className="flex items-center gap-2">
              <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <Input value={editForm.website || ''} onChange={e => setEditForm(p => ({ ...p, website: e.target.value }))} placeholder="Website URL" />
            </div>
            <div className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <Input value={editForm.documentation_url || ''} onChange={e => setEditForm(p => ({ ...p, documentation_url: e.target.value }))} placeholder="Documentation URL" />
            </div>
            <div className="flex items-center gap-2">
              <PlayCircle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <Input value={editForm.demo_url || ''} onChange={e => setEditForm(p => ({ ...p, demo_url: e.target.value }))} placeholder="Demo / Trial URL" />
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <Input value={editForm.pricing_url || ''} onChange={e => setEditForm(p => ({ ...p, pricing_url: e.target.value }))} placeholder="Pricing URL" />
            </div>
            <div className="flex items-center gap-2">
              <LifeBuoy className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <Input value={editForm.support_url || ''} onChange={e => setEditForm(p => ({ ...p, support_url: e.target.value }))} placeholder="Support URL" />
            </div>
          </div>
        </div>
        <div>
          <Label className="text-xs">Notes</Label>
          <Textarea value={editForm.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Internal notes..." />
        </div>
        <div>
          <Label className="text-xs">Tags</Label>
          <TagInput
            value={editForm.tags || []}
            onChange={tags => setEditForm(p => ({ ...p, tags }))}
            token={token}
          />
        </div>
      </div>
    );
  }
}


function VendorCombobox({ vendors, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const selectedVendor = vendors.find(v => v.id === value);
  const filtered = useMemo(() => {
    if (!searchValue) return vendors.slice(0, 50);
    const q = searchValue.toLowerCase();
    return vendors.filter(v => v.name.toLowerCase().includes(q)).slice(0, 50);
  }, [vendors, searchValue]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal h-9 text-sm"
          data-testid="vendor-combobox"
        >
          <span className="truncate">
            {selectedVendor ? selectedVendor.name : 'Select vendor...'}
          </span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search vendors..."
            value={searchValue}
            onValueChange={setSearchValue}
            data-testid="vendor-combobox-search"
          />
          <CommandList>
            <CommandEmpty>No vendor found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="none"
                onSelect={() => { onChange(''); setOpen(false); setSearchValue(''); }}
              >
                <Check className={`mr-2 h-3.5 w-3.5 ${!value ? 'opacity-100' : 'opacity-0'}`} />
                No vendor
              </CommandItem>
              {filtered.map(v => (
                <CommandItem
                  key={v.id}
                  value={v.id}
                  onSelect={() => { onChange(v.id); setOpen(false); setSearchValue(''); }}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${value === v.id ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="truncate">{v.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


function ProductMultiSelect({ products, platformsByCategory, platforms, categories, value, excludeId, onChange }) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const selectedProducts = useMemo(() =>
    value.map(id => products.find(p => p.id === id)).filter(Boolean),
    [value, products]
  );

  const filtered = useMemo(() => {
    let list = products.filter(p => p.id !== excludeId);
    if (searchValue) {
      const q = searchValue.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || (p.vendor_name || '').toLowerCase().includes(q));
    }
    return list.slice(0, 50);
  }, [products, searchValue, excludeId]);

  // Group by category for display
  const groupedFiltered = useMemo(() => {
    const groups = {};
    filtered.forEach(p => {
      const cat = p.category_name || 'Uncategorized';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const toggle = (productId) => {
    const next = value.includes(productId)
      ? value.filter(id => id !== productId)
      : [...value, productId];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {selectedProducts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedProducts.map(p => (
            <Badge key={p.id} variant="secondary" className="text-xs gap-1 pr-1">
              <Layers className="h-3 w-3 text-orange-500" />
              {p.name}
              <button onClick={() => toggle(p.id)} className="ml-0.5 hover:text-destructive rounded-full p-0.5">×</button>
            </Badge>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between font-normal h-9 text-sm" data-testid="white-label-product-select">
            <span className="truncate text-muted-foreground">
              {value.length > 0 ? `${value.length} product${value.length > 1 ? 's' : ''} selected` : 'Select parent product(s)...'}
            </span>
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[350px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search products..."
              value={searchValue}
              onValueChange={setSearchValue}
              data-testid="white-label-search"
            />
            <CommandList>
              <CommandEmpty>No products found.</CommandEmpty>
              {groupedFiltered.map(([catName, catProducts]) => (
                <CommandGroup key={catName} heading={catName}>
                  {catProducts.map(p => (
                    <CommandItem key={p.id} value={p.id} onSelect={() => toggle(p.id)}>
                      <Check className={`mr-2 h-3.5 w-3.5 ${value.includes(p.id) ? 'opacity-100' : 'opacity-0'}`} />
                      <div className="flex-1 min-w-0">
                        <span className="truncate text-sm">{p.name}</span>
                        <span className="text-[10px] text-muted-foreground ml-1.5">{p.platform_name}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}


function BrandDesignationEditor({ designations, brands, productId, headers, onUpdate }) {
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Group designations by parent company
  const grouped = useMemo(() => {
    const brandMap = {};
    brands.forEach(b => { brandMap[b.id] = b; });
    const groups = {};
    designations.forEach(bd => {
      const brand = brandMap[bd.brand_id];
      const parent = brand?.parent_company || 'Other';
      if (!groups[parent]) groups[parent] = { brands: [], designations: [] };
      groups[parent].designations.push(bd);
    });
    return groups;
  }, [designations, brands]);

  // Get summary flags for a group (what's common across all entries)
  const groupSummary = (desigs) => {
    if (desigs.length === 0) return {};
    const allRequired = desigs.every(d => d.required);
    const allPreferred = desigs.every(d => d.preferred);
    const allApproved = desigs.every(d => d.approved);
    const allGrandfathered = desigs.every(d => d.grandfathered);
    const someRequired = desigs.some(d => d.required);
    const somePreferred = desigs.some(d => d.preferred);
    const someApproved = desigs.some(d => d.approved);
    const someGrandfathered = desigs.some(d => d.grandfathered);
    return { allRequired, allPreferred, allApproved, allGrandfathered, someRequired, somePreferred, someApproved, someGrandfathered };
  };

  const toggleGroupFlag = async (parent, flag) => {
    const desigs = grouped[parent]?.designations || [];
    const allSet = desigs.every(d => d[flag]);
    const newVal = !allSet;
    for (const bd of desigs) {
      await axios.post(`${BACKEND_URL}/api/admin/brand-designations`, {
        product_id: productId, brand_id: bd.brand_id, date: bd.date || '',
        required: flag === 'required' ? newVal : bd.required || false,
        preferred: flag === 'preferred' ? newVal : bd.preferred || false,
        approved: flag === 'approved' ? newVal : bd.approved || false,
        grandfathered: flag === 'grandfathered' ? newVal : bd.grandfathered || false,
      }, { headers });
    }
    onUpdate(designations.map(bd => {
      const brand = brands.find(b => b.id === bd.brand_id);
      if ((brand?.parent_company || 'Other') === parent) return { ...bd, [flag]: newVal };
      return bd;
    }));
    toast.success(`${newVal ? 'Set' : 'Cleared'} ${flag} for ${parent}`);
  };

  const updateGroupDate = async (parent, date) => {
    const desigs = grouped[parent]?.designations || [];
    for (const bd of desigs) {
      await axios.post(`${BACKEND_URL}/api/admin/brand-designations`, {
        product_id: productId, brand_id: bd.brand_id, date,
        required: bd.required || false, preferred: bd.preferred || false,
        approved: bd.approved || false, grandfathered: bd.grandfathered || false,
      }, { headers });
    }
    onUpdate(designations.map(bd => {
      const brand = brands.find(b => b.id === bd.brand_id);
      if ((brand?.parent_company || 'Other') === parent) return { ...bd, date };
      return bd;
    }));
  };

  const removeGroup = async (parent) => {
    const desigs = grouped[parent]?.designations || [];
    if (!window.confirm(`Remove all ${desigs.length} ${parent} brand designations?`)) return;
    for (const bd of desigs) {
      await axios.delete(`${BACKEND_URL}/api/admin/brand-designations`, { headers, params: { product_id: productId, brand_id: bd.brand_id } });
    }
    onUpdate(designations.filter(bd => {
      const brand = brands.find(b => b.id === bd.brand_id);
      return (brand?.parent_company || 'Other') !== parent;
    }));
    toast.success(`Removed ${parent}`);
  };

  const updateSingleFlag = async (bd, flag, val) => {
    const updated = { ...bd, [flag]: val };
    await axios.post(`${BACKEND_URL}/api/admin/brand-designations`, {
      product_id: productId, brand_id: bd.brand_id, date: bd.date || '',
      required: updated.required || false, preferred: updated.preferred || false,
      approved: updated.approved || false, grandfathered: updated.grandfathered || false,
    }, { headers });
    onUpdate(designations.map(b => b.brand_id === bd.brand_id ? updated : b));
  };

  const removeSingle = async (bd) => {
    await axios.delete(`${BACKEND_URL}/api/admin/brand-designations`, { headers, params: { product_id: productId, brand_id: bd.brand_id } });
    onUpdate(designations.filter(b => b.brand_id !== bd.brand_id));
    toast.success('Removed');
  };

  // Add brands
  const addBrands = async (brandsToAdd) => {
    const date = new Date().toISOString().split('T')[0];
    for (const b of brandsToAdd) {
      await axios.post(`${BACKEND_URL}/api/admin/brand-designations`, {
        product_id: productId, brand_id: b.id, approved: true, date,
      }, { headers });
    }
    onUpdate([...designations, ...brandsToAdd.map(b => ({
      brand_id: b.id, brand_name: b.name, brand_color: b.color || '#6366F1', approved: true, date,
    }))]);
    toast.success(`Added ${brandsToAdd.length} brand${brandsToAdd.length > 1 ? 's' : ''}`);
  };

  const FLAGS = [
    { key: 'required', label: 'Req' },
    { key: 'preferred', label: 'Pref' },
    { key: 'approved', label: 'Appr' },
    { key: 'grandfathered', label: 'GF' },
  ];

  return (
    <div className="space-y-2 p-3 rounded-lg border bg-muted/10">
      <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Brand Designations</Label>
      <div className="space-y-1.5">
        {Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0])).map(([parent, group]) => {
          const summary = groupSummary(group.designations);
          const isExpanded = expandedGroups.has(parent);
          const commonDate = group.designations.length > 0 && group.designations.every(d => d.date === group.designations[0].date) ? group.designations[0].date : '';
          return (
            <div key={parent} className="rounded-lg border bg-card overflow-hidden">
              {/* Group header */}
              <div className="flex items-center gap-2 p-2.5 text-xs">
                <button onClick={() => setExpandedGroups(prev => {
                  const n = new Set(prev); n.has(parent) ? n.delete(parent) : n.add(parent); return n;
                })} className="shrink-0">
                  <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${!isExpanded ? '-rotate-90' : ''}`} />
                </button>
                <Shield className="h-3.5 w-3.5 shrink-0 text-indigo-500" />
                <span className="font-semibold flex-1">{parent}</span>
                <Badge variant="secondary" className="text-[9px] font-mono">{group.designations.length}</Badge>
                <Input type="date" value={commonDate} className="h-6 w-28 text-[10px]" onChange={e => updateGroupDate(parent, e.target.value)} title="Set date for all" />
                {FLAGS.map(f => (
                  <label key={f.key} className="flex items-center gap-0.5 cursor-pointer" title={`${f.key} for all ${parent} brands`}>
                    <Checkbox checked={summary[`all${f.key.charAt(0).toUpperCase() + f.key.slice(1)}`] || false}
                      className={summary[`some${f.key.charAt(0).toUpperCase() + f.key.slice(1)}`] && !summary[`all${f.key.charAt(0).toUpperCase() + f.key.slice(1)}`] ? 'opacity-50' : ''}
                      onCheckedChange={() => toggleGroupFlag(parent, f.key)} />
                    <span className="text-[9px]">{f.label}</span>
                  </label>
                ))}
                <button onClick={() => removeGroup(parent)} className="text-destructive hover:bg-destructive/10 rounded p-0.5"><Trash2 className="h-3 w-3" /></button>
              </div>
              {/* Expanded individual brands */}
              {isExpanded && (
                <div className="border-t px-3 py-1.5 space-y-0.5 bg-muted/10">
                  {group.designations.sort((a, b) => (a.brand_name || '').localeCompare(b.brand_name || '')).map(bd => (
                    <div key={bd.brand_id} className="flex items-center gap-2 py-1 text-[11px] group/bd">
                      <span className="flex-1 truncate pl-5">{bd.brand_name}</span>
                      <Input type="date" value={bd.date || ''} className="h-5 w-28 text-[9px] opacity-60 group-hover/bd:opacity-100" onChange={async (e) => {
                        await axios.post(`${BACKEND_URL}/api/admin/brand-designations`, {
                          product_id: productId, brand_id: bd.brand_id, date: e.target.value,
                          required: bd.required || false, preferred: bd.preferred || false,
                          approved: bd.approved || false, grandfathered: bd.grandfathered || false,
                        }, { headers });
                        onUpdate(designations.map(b => b.brand_id === bd.brand_id ? { ...b, date: e.target.value } : b));
                      }} />
                      {FLAGS.map(f => (
                        <label key={f.key} className="flex items-center gap-0.5 cursor-pointer">
                          <Checkbox checked={bd[f.key] || false} onCheckedChange={v => updateSingleFlag(bd, f.key, !!v)} className="h-3.5 w-3.5" />
                          <span className="text-[8px]">{f.label}</span>
                        </label>
                      ))}
                      <button onClick={() => removeSingle(bd)} className="text-destructive hover:bg-destructive/10 rounded p-0.5 opacity-0 group-hover/bd:opacity-100"><Trash2 className="h-2.5 w-2.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Add brand dropdown */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="text-xs gap-1 h-7" data-testid="add-brand-designation-products">
            <Plus className="h-3 w-3" /> Add Brand / Brand Family
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search brands..." />
            <CommandList>
              <CommandEmpty>No brands found.</CommandEmpty>
              {(() => {
                const groups = {};
                const existingIds = new Set(designations.map(b => b.brand_id));
                brands.filter(b => !existingIds.has(b.id)).forEach(b => {
                  const parent = b.parent_company || 'Other';
                  if (!groups[parent]) groups[parent] = [];
                  groups[parent].push(b);
                });
                return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0])).map(([parent, bList]) => (
                  <CommandGroup key={parent} heading={
                    <button className="w-full text-left flex items-center justify-between hover:text-primary transition-colors" onClick={async (e) => {
                      e.preventDefault(); e.stopPropagation();
                      await addBrands(bList);
                    }}>
                      <span>{parent}</span>
                      <span className="text-[9px] font-normal text-primary hover:underline">+ Add all {bList.length}</span>
                    </button>
                  }>
                    {bList.sort((a, b) => a.name.localeCompare(b.name)).map(b => (
                      <CommandItem key={b.id} value={b.name} onSelect={() => addBrands([b])}>
                        <span className="text-sm">{b.name}</span>
                        {b.chain_scale && <span className="text-[9px] text-muted-foreground ml-1.5">{b.chain_scale}</span>}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ));
              })()}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
